"use client";
import { useRef, useState } from "react";
import usePublicContacts, { phoneTel, phoneDisplay } from '@/hooks/usePublicContacts';
import { motion, useInView, AnimatePresence, Variants } from "framer-motion";
import { Send, Phone, Mail, User, School, Check, CalendarCheck, MessageSquare } from "lucide-react";
import DropdownBase from "@/components/ui/Dropdown";
import { trackEvent } from "@/utils/analytics";
import {
  validateLeadForm,
  sanitizePhoneInput,
  normalizeIndianMobile,
} from "@/utils/contactValidation";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Dropdown = DropdownBase as any;

const primaryColor = "#3D5EE1";

// The card serves two intents from the same form. `demo` keeps the original
// "book a walkthrough" flow; `enquiry` swaps the students dropdown for a free
// text message so people with a plain question aren't forced to pick a range.
const REQUEST_TYPES = [
  { id: "demo", label: "Free Demo", icon: CalendarCheck },
  { id: "enquiry", label: "Enquiry", icon: MessageSquare },
] as const;

type RequestType = (typeof REQUEST_TYPES)[number]["id"];

const emptyForm = { name: "", school: "", email: "", phone: "", students: "", message: "" };

export default function DemoSection() {
  const contacts = usePublicContacts();
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-20px" });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [requestType, setRequestType] = useState<RequestType>("demo");
  const [form, setForm] = useState(emptyForm);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  // Per-field messages, shown under the offending input. Populated on
  // submit, then cleared per-field as the visitor fixes each one.
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEnquiry = requestType === "enquiry";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    // The server validates all of this again — it has to, this endpoint is
    // public — but catching it here keeps the message next to the field.
    const fieldErrors = validateLeadForm(form, { isEnquiry });
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      setSubmitError(null);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL || "";
      const res = await fetch(`${apiBase}/api/demo-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          schoolName: form.school,
          email: form.email,
          phone: normalizeIndianMobile(form.phone),
          studentsRange: isEnquiry ? "" : form.students,
          message: isEnquiry ? form.message : "",
          requestType,
          source: "marketing",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Failed to submit your request. Please try again.");
      }
      setSubmitted(true);
      // Counted as a conversion in GA4 via GTM (Custom Event trigger).
      trackEvent("lead_submitted", { request_type: requestType });
      setForm(emptyForm);
      setErrors({});
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    "Live walkthrough of all features",
    "Custom setup for your school size",
    "Q&A with a product specialist",
    "No credit card needed",
  ];

  const leftVariants: Variants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, type: "spring", bounce: 0.2 } }
  };

  const rightVariants: Variants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, type: "spring", bounce: 0.2, delay: 0.2 } }
  };

  const listVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.4 } }
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
  };

  // Top padding lives on the <section> and nowhere else. The grid inside it
  // used to add its own `pt-10` on top, which stacked to 120px of dead space
  // between the fixed header and the content.
  return (
    <section ref={ref} id="contact" className="w-full pt-10 pb-0 lg:pt-[100px] lg:pb-0 relative overflow-hidden bg-[#F7F8FE]">
      
      {/* Decorative Top Left Shape */}
      <div className="absolute top-0 left-0 w-[500px] h-[300px] pointer-events-none opacity-40">
        <svg viewBox="0 0 500 300" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 0 H500 C400 100, 200 50, 0 200 V0 Z" fill="#DADFF9"/>
        </svg>
      </div>

      <div className="site-container relative z-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">

          {/* ── Left: Info ── */}
          <motion.div
            variants={leftVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="flex flex-col gap-6 relative z-20"
          >
            <div>
              {/* Deliberately NOT `.section-heading`. This is the page headline of
                 /contact, not a mid-page section opener, so it takes the hero ramp
                 the other two page heroes use (Hero, FeaturesHero). The section
                 scale bottoms out at 22/25px, which on a phone left this <h1>
                 within a few px of the 16px paragraph under it.
                 `lg` rejoins the section scale at 48px — past 1024px the copy
                 moves into a half-width column beside the form, and anything
                 larger wraps "See Kaympus" onto three lines. */}
              <h1 className="font-heading font-light text-[32px] min-[380px]:text-[36px] sm:text-[44px] lg:text-[48px] leading-[1.14] tracking-[-0.035em] text-[#1E293B]">
                See Kaympus <br/><span className="section-heading-accent">in action</span>
              </h1>
            </div>

            <p className="text-[16px] text-slate-500 font-medium leading-relaxed max-w-[480px]">
              Get a personalised 30-minute walkthrough tailored to your school's size and requirements. Our specialists will show you exactly how Kaympus can solve your biggest challenges.
            </p>

            <motion.ul
              variants={listVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="flex flex-col gap-5"
            >
              {benefits.map((b, i) => (
                <motion.li key={i} variants={itemVariants} className="flex items-center gap-4">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-[#3D5EE1] shadow-sm">
                    <Check size={14} strokeWidth={3} className="text-white" />
                  </div>
                  <span className="text-[15px] text-slate-700 font-medium">{b}</span>
                </motion.li>
              ))}
              
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <motion.a
                  whileHover={{ y: -2 }}
                  href={`tel:${phoneTel(contacts.supportPhone)}`}
                  className="flex items-center gap-3 px-5 py-3 bg-white rounded-xl border border-brand-100 shadow-sm transition-all hover:shadow-md hover:border-brand-300 group"
                >
                  <Phone size={16} className="text-[#3D5EE1] group-hover:scale-110 transition-transform" />
                  <span className="text-[14px] font-bold text-slate-700">{phoneDisplay(contacts.supportPhone)}</span>
                </motion.a>
                <motion.a
                  whileHover={{ y: -2 }}
                  href={`mailto:${contacts.salesEmail}`}
                  className="flex items-center gap-3 px-5 py-3 bg-white rounded-xl border border-brand-100 shadow-sm transition-all hover:shadow-md hover:border-brand-300 group"
                >
                  <Mail size={16} className="text-[#3D5EE1] group-hover:scale-110 transition-transform" />
                  <span className="text-[14px] font-bold text-slate-700">{contacts.salesEmail}</span>
                </motion.a>
              </div>
            </motion.ul>
          </motion.div>

          {/* ── Right: Form ── */}
          <motion.div
            variants={rightVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="w-full max-w-[500px] justify-self-center lg:justify-self-start relative z-20"
          >
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-xl relative overflow-hidden">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.5, type: "spring" }}
                    className="flex flex-col items-center text-center gap-5 py-12"
                  >
                    <div className="w-20 h-20 rounded-full bg-brand-50 flex items-center justify-center mb-2">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.2, type: "spring", bounce: 0.5 }}
                      >
                        <Check size={40} className="text-[#3D5EE1]" strokeWidth={3} />
                      </motion.div>
                    </div>
                    <h3 className="font-bold text-[24px] text-slate-800">
                      {isEnquiry ? "Enquiry Received!" : "Request Received!"}
                    </h3>
                    <p className="text-[15px] text-slate-500 font-medium leading-relaxed max-w-[340px]">
                      {isEnquiry
                        ? "Thanks for reaching out. Our team will get back to you within 24 hours with an answer. Check your email!"
                        : "Our team will reach out within 24 hours to schedule your personalised demo. Check your email!"}
                    </p>
                    <button onClick={() => setSubmitted(false)} className="mt-6 px-6 py-2.5 rounded-full border border-brand-500 text-[14px] font-bold text-brand-600 hover:bg-brand-50 transition-colors">
                      Submit another request
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 className="font-extrabold text-[22px] text-slate-800 mb-5 text-center sm:text-left">
                      {isEnquiry ? "Send us an enquiry" : "Request your free demo"}
                    </h3>

                    {/* Intent switch. The sliding pill is a `layoutId` element so the
                       two labels stay legible instead of cross-fading. */}
                    <div
                      role="tablist"
                      aria-label="What would you like to do?"
                      className="flex p-1 mb-6 rounded-2xl bg-slate-100/80 border border-slate-100"
                    >
                      {REQUEST_TYPES.map(type => {
                        const TypeIcon = type.icon;
                        const active = requestType === type.id;
                        return (
                          <button
                            key={type.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => {
                              setRequestType(type.id);
                              setSubmitError(null);
                              // The two intents validate different fields —
                              // keeping stale errors would flag a box that
                              // is no longer on screen.
                              setErrors({});
                            }}
                            className="relative flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] font-bold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5EE1]/40"
                          >
                            {active && (
                              <motion.span
                                layoutId="demo-request-type-pill"
                                transition={{ type: "spring", stiffness: 400, damping: 32 }}
                                className="absolute inset-0 rounded-xl bg-white shadow-sm border border-slate-100"
                              />
                            )}
                            <span className={`relative flex items-center gap-2 ${active ? "text-[#3D5EE1]" : "text-slate-500 hover:text-slate-700"}`}>
                              <TypeIcon size={15} />
                              {type.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                      {[
                        { icon: User, id: "name", label: "Your Name", placeholder: "e.g. Rajesh Kumar", type: "text" },
                        { icon: School, id: "school", label: "School Name", placeholder: "e.g. DAV Public School", type: "text" },
                        { icon: Mail, id: "email", label: "Email Address", placeholder: "you@school.com", type: "email" },
                        { icon: Phone, id: "phone", label: "Phone Number", placeholder: "+91 98765 43210", type: "tel" },
                      ].map(field => {
                        const Icon = field.icon;
                        const isFocused = focusedField === field.id;
                        const error = errors[field.id];
                        return (
                          <div key={field.id} className="relative">
                            <label htmlFor={`lead-${field.id}`} className="block text-[11px] font-bold text-slate-400 mb-1.5 uppercase tracking-widest">{field.label}</label>
                            <div className="relative group">
                              <motion.div
                                className="absolute left-4 top-1/2 -translate-y-1/2 z-10"
                                animate={{ color: error ? "#EF4444" : isFocused ? "#3D5EE1" : "#94a3b8" }}
                              >
                                <Icon size={16} />
                              </motion.div>
                              <input
                                id={`lead-${field.id}`}
                                type={field.type}
                                required
                                {...(field.id === "phone"
                                  ? { inputMode: "tel" as const, autoComplete: "tel", maxLength: 20 }
                                  : {})}
                                aria-invalid={!!error}
                                aria-describedby={error ? `lead-${field.id}-error` : undefined}
                                placeholder={field.placeholder}
                                value={form[field.id as keyof typeof form]}
                                onChange={e => {
                                  // Letters are stripped from the phone box as they
                                  // are typed — `type="tel"` enforces nothing, and
                                  // silently accepting "asdf" produced leads nobody
                                  // could call.
                                  const value = field.id === "phone"
                                    ? sanitizePhoneInput(e.target.value)
                                    : e.target.value;
                                  setForm({ ...form, [field.id]: value });
                                  if (error) setErrors(prev => {
                                    const next = { ...prev };
                                    delete next[field.id];
                                    return next;
                                  });
                                }}
                                onFocus={() => setFocusedField(field.id)}
                                onBlur={() => setFocusedField(null)}
                                className="w-full pl-12 pr-4 py-3 rounded-xl border text-[14px] font-medium text-slate-800 placeholder-slate-300 focus:outline-none transition-all duration-300 bg-white"
                                style={{
                                  borderColor: error ? "#EF4444" : isFocused ? "#3D5EE1" : "#E2E8F0",
                                  boxShadow: isFocused && !error ? "0 4px 15px rgba(61,94,225,0.1)" : "none"
                                }}
                              />
                            </div>
                            {error && (
                              <p id={`lead-${field.id}-error`} role="alert" className="mt-1.5 text-[12px] font-medium text-red-500">
                                {error}
                              </p>
                            )}
                          </div>
                        );
                      })}

                      {isEnquiry ? (
                        <div className="mb-2">
                          <label className="block text-[11px] font-bold text-slate-400 mb-1.5 uppercase tracking-widest">Your Message</label>
                          <div className="relative">
                            <motion.div
                              className="absolute left-4 top-3.5 z-10"
                              animate={{ color: focusedField === "message" ? "#3D5EE1" : "#94a3b8" }}
                            >
                              <MessageSquare size={16} />
                            </motion.div>
                            <textarea
                              required
                              rows={4}
                              maxLength={2000}
                              aria-invalid={!!errors.message}
                              aria-describedby={errors.message ? "lead-message-error" : undefined}
                              placeholder="Tell us what you'd like to know about Kaympus..."
                              value={form.message}
                              onChange={e => {
                                setForm({ ...form, message: e.target.value });
                                if (errors.message) setErrors(prev => {
                                  const next = { ...prev };
                                  delete next.message;
                                  return next;
                                });
                              }}
                              onFocus={() => setFocusedField("message")}
                              onBlur={() => setFocusedField(null)}
                              className="w-full pl-12 pr-4 py-3 rounded-xl border text-[14px] font-medium text-slate-800 placeholder-slate-300 focus:outline-none transition-all duration-300 bg-white resize-none"
                              style={{
                                borderColor: errors.message ? "#EF4444" : focusedField === "message" ? primaryColor : "#E2E8F0",
                                boxShadow: focusedField === "message" && !errors.message ? "0 4px 15px rgba(61,94,225,0.1)" : "none"
                              }}
                            />
                          </div>
                          {errors.message && (
                            <p id="lead-message-error" role="alert" className="mt-1.5 text-[12px] font-medium text-red-500">
                              {errors.message}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="mb-2">
                          <label className="block text-[11px] font-bold text-slate-400 mb-1.5 uppercase tracking-widest">Number of Students</label>
                          <Dropdown
                            value={form.students}
                            options={["Under 500", "500 – 1,000", "1,000 – 2,500", "2,500 – 5,000", "5,000+"]}
                            onChange={(opt: any) => {
                              setForm({ ...form, students: typeof opt === 'object' ? opt.label : opt });
                              if (errors.students) setErrors(prev => {
                                const next = { ...prev };
                                delete next.students;
                                return next;
                              });
                            }}
                            placeholder="Select range..."
                            layout_width="full"
                            border_border={errors.students ? "border border-red-400" : undefined}
                          />
                          {errors.students && (
                            <p role="alert" className="mt-1.5 text-[12px] font-medium text-red-500">
                              {errors.students}
                            </p>
                          )}
                        </div>
                      )}

                      {submitError && (
                        <p className="text-center text-[12px] text-red-500 font-medium -mt-2 mb-1">
                          {submitError}
                        </p>
                      )}

                      <motion.button
                        whileHover={{ scale: submitting ? 1 : 1.02 }}
                        whileTap={{ scale: submitting ? 1 : 0.98 }}
                        type="submit"
                        disabled={submitting}
                        className="mt-1 w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-[15px] font-bold text-white shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed bg-[#3D5EE1] hover:bg-[#2040CF]"
                      >
                        <Send size={16} />
                        {submitting ? "Submitting..." : isEnquiry ? "Send My Enquiry" : "Book My Free Demo"}
                      </motion.button>

                      <p className="text-center text-[11px] text-slate-400 font-medium mt-2">
                        By submitting, you agree to our Privacy Policy. <br className="hidden sm:block" />We'll never spam you.
                      </p>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Bottom Landscape SVG */}
      <div className="absolute bottom-0 left-0 w-[120%] lg:w-[100%] pointer-events-none z-0 transform translate-y-2">
        <svg viewBox="0 0 1440 320" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto text-[#CAD3F7]">
          {/* Back wavy hill */}
          <path d="M0,256L48,229.3C96,203,192,149,288,144C384,139,480,181,576,197.3C672,213,768,203,864,186.7C960,171,1056,149,1152,149.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" fill="#DADFF9"/>
          
          {/* Middle wavy hill */}
          <path d="M0,288L48,272C96,256,192,224,288,218.7C384,213,480,235,576,234.7C672,235,768,213,864,197.3C960,181,1056,171,1152,186.7C1248,203,1344,245,1392,266.7L1440,288L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" fill="#BEC8F5"/>
          
          {/* Front wavy hill */}
          <path d="M0,320L48,304C96,288,192,256,288,245.3C384,235,480,245,576,261.3C672,277,768,299,864,288C960,277,1056,235,1152,213.3C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" fill="#3D5EE1" opacity="0.15"/>

          {/* School Building Center-ish */}
          <g transform="translate(450, 80)">
            {/* Base building */}
            <rect x="50" y="100" width="140" height="80" fill="#BEC8F5" stroke="#FFFFFF" strokeWidth="2"/>
            {/* Left Wing */}
            <path d="M20 120 L50 120 L50 180 L20 180 Z" fill="#DADFF9" stroke="#FFFFFF" strokeWidth="2"/>
            <path d="M10 90 L50 120 L20 120 Z" fill="#BEC8F5"/>
            {/* Right Wing */}
            <path d="M190 120 L220 120 L220 180 L190 180 Z" fill="#DADFF9" stroke="#FFFFFF" strokeWidth="2"/>
            <path d="M230 90 L190 120 L220 120 Z" fill="#BEC8F5"/>
            
            {/* Clock Tower */}
            <rect x="100" y="40" width="40" height="60" fill="#BEC8F5" stroke="#FFFFFF" strokeWidth="2"/>
            <path d="M95 40 L120 10 L145 40 Z" fill="#3D5EE1" opacity="0.5"/>
            <circle cx="120" cy="70" r="12" fill="#FFFFFF" stroke="#3D5EE1" strokeWidth="2" opacity="0.8"/>
            <path d="M120 70 L120 62 M120 70 L125 70" stroke="#3D5EE1" strokeWidth="2"/>
            
            {/* Main Door */}
            <rect x="105" y="140" width="30" height="40" fill="#FFFFFF" rx="4"/>
            <path d="M105 140 C105 125, 135 125, 135 140" fill="#FFFFFF"/>
            
            {/* Windows */}
            <rect x="65" y="120" width="15" height="20" fill="#FFFFFF" rx="2" opacity="0.6"/>
            <rect x="65" y="150" width="15" height="20" fill="#FFFFFF" rx="2" opacity="0.6"/>
            <rect x="160" y="120" width="15" height="20" fill="#FFFFFF" rx="2" opacity="0.6"/>
            <rect x="160" y="150" width="15" height="20" fill="#FFFFFF" rx="2" opacity="0.6"/>
          </g>

          {/* Simple Trees */}
          <g transform="translate(380, 140)">
            <path d="M20 70 L20 100" stroke="#3D5EE1" strokeWidth="4" opacity="0.3"/>
            <circle cx="20" cy="50" r="25" fill="#BEC8F5"/>
          </g>
          <g transform="translate(680, 120)">
            <path d="M20 80 L20 110" stroke="#3D5EE1" strokeWidth="4" opacity="0.3"/>
            <path d="M0 80 C0 40, 40 40, 40 80 Z" fill="#3D5EE1" opacity="0.4"/>
          </g>
        </svg>
      </div>

    </section>
  );
}
